/**
 * Dashboard page - Shows user's simulations and models
 */

import { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { getSimulations } from '../services/api';
import type { Simulation } from '../types/simulation';
import { useNotification } from '../contexts/NotificationContext';
import './DashboardPage.css';

interface DashboardPageProps {
  onSelectSimulation: (simulation: Simulation) => void;
  onNavigateToBrowser: () => void;
  setTitle: (title: string) => void;
}

export function DashboardPage({ onSelectSimulation, onNavigateToBrowser, setTitle }: DashboardPageProps) {
  const { showNotification } = useNotification();

  useEffect(() => {
    setTitle('DASHBOARD');
  }, [setTitle]);
  const [simulations, setSimulations] = useState<Simulation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSimulations = async () => {
    setIsLoading(true);
    try {
      const data = await getSimulations();
      setSimulations(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load simulations');
      console.error('Failed to fetch simulations:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSimulations();
  }, []);

  // Create portal content for toolbar
  const portalTarget = document.getElementById('global-toolbar-portal');
  const toolbarContent = portalTarget ? ReactDOM.createPortal(
    <div className="toolbar-left">
      <div className="toolbar-link" onClick={onNavigateToBrowser}>
        browse simulations
      </div>
    </div>,
    portalTarget
  ) : null;

  const handleCopyLink = (e: React.MouseEvent, simId: string) => {
    e.stopPropagation();
    const url = new URL(window.location.origin);
    url.searchParams.set('sim', simId);
    navigator.clipboard.writeText(url.toString());
    showNotification('Link copied to clipboard!');
  };

  const handleCopySimId = (e: React.MouseEvent, simId: string) => {
    e.stopPropagation();
    navigator.clipboard.writeText(simId);
    showNotification('Simulation ID copied!');
  };

  return (
    <div className="dashboard-page">
      {toolbarContent}

      {isLoading && (
        <div className="dashboard-loading">
          <div className="loading-spinner"></div>
          <p>&gt; LOADING YOUR DASHBOARD...</p>
        </div>
      )}

      {error && (
        <div className="dashboard-error">
          <p>ERROR: {error}</p>
          <button onClick={fetchSimulations}>RETRY</button>
        </div>
      )}

      {!isLoading && !error && (
        <div className="dashboard-content">
        <div className="dashboard-section">
          <div className="section-header">
            <h2>YOUR SIMULATIONS</h2>
            <span className="simulation-count">
              {simulations.length} {simulations.length === 1 ? 'SIMULATION' : 'SIMULATIONS'}
            </span>
          </div>

          {simulations.length === 0 ? (
            <div className="dashboard-empty">
              <p>&gt; NO SIMULATIONS YET</p>
              <p className="empty-hint">
                Browse available simulations or create your own to get started
              </p>
              <button className="browse-cta" onClick={onNavigateToBrowser}>
                BROWSE SIMULATIONS →
              </button>
            </div>
          ) : (
            <div className="simulation-grid">
              {simulations.map((sim) => {
                const aiModels = sim.ai_models || [];
                return (
                  <button
                    key={sim.id}
                    className={`simulation-card simulation-card--${sim.status}`}
                    onClick={() => onSelectSimulation(sim)}
                    disabled={sim.status !== 'active'}
                  >
                    <button
                      className="card-share-btn"
                      onClick={(e) => handleCopySimId(e, sim.id)}
                      title="Copy simulation ID"
                      aria-label="Copy simulation ID"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                      </svg>
                    </button>
                    <button
                      className="card-share-btn"
                      onClick={(e) => handleCopyLink(e, sim.id)}
                      title="Copy sharing link"
                      aria-label="Copy sharing link"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
                        <polyline points="16 6 12 2 8 6"></polyline>
                        <line x1="12" y1="2" x2="12" y2="15"></line>
                      </svg>
                    </button>
                    <div className="card-content">
                      <h3 className="card-title">{sim.name}</h3>
                      {sim.description && <p className="card-description">{sim.description}</p>}
                      <div className="card-meta">
                        <span className={`status-badge status-badge--${sim.status}`}>
                          {sim.status.toUpperCase()}
                        </span>
                        {aiModels.length > 0 && (
                          <span className="ai-count">
                            {aiModels.length} AI Model{aiModels.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      {aiModels.length > 0 && (
                        <div className="card-ai-list">
                          {aiModels.map((ai, idx) => (
                            <span key={idx} className="ai-badge">
                              {ai.ai_name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        </div>
      )}
    </div>
  );
}
